import { useEffect, useState } from 'react';

/**
 * READY store (RD-NOTICES-V2) — true once this session's receipts have been
 * hydrated SUCCESSFULLY from the server at the session's final assurance level
 * (a 2FA user is read at aal2, never at the MFA-gated aal1 that returns empty).
 * Every greeting/auto-open gate waits for this before deciding to open: on a
 * brand-new device the local cache is empty, so opening before hydration
 * would re-greet the user with something they already saw and dismissed on
 * another device — the exact bug this feature exists to fix.
 *
 * There is NO "greet anyway" timeout (removed in RD-SESS-034, 2026-10-01):
 * on a dead network a greeting stays silent and retries on the next
 * resume/auth event — silence is the safe direction, a re-greet is the bug.
 * Not every consumer is one-time: the 2FA nudge row (MfaEncourageBanner) is a
 * persistent security nudge and is hidden while not ready — acceptable only
 * because each read is bounded (NoticeReceiptsSync HYDRATE_TIMEOUT_MS) and
 * retried, and required-2FA roles are enforced by the route gates regardless.
 */
let ready = false;
const listeners = new Set();

export function isNoticeReceiptsReady() {
  return ready;
}

export function setNoticeReceiptsReady(value) {
  const next = Boolean(value);
  if (ready === next) return;
  ready = next;
  listeners.forEach((listener) => {
    try { listener(ready); } catch (_e) { /* never break the others */ }
  });
}

export function useNoticeReceiptsReady() {
  const [state, setState] = useState(ready);
  useEffect(() => {
    // The store may have flipped between render and this effect committing.
    setState(ready);
    const listener = (value) => setState(value);
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  }, []);
  return state;
}

/** Test-only reset — a signed-out session's next sign-in must not inherit a
 *  previous session's READY flag. */
export function __resetNoticeReceiptsReady() {
  ready = false;
  listeners.clear();
}
